import React, { useState, useEffect, useMemo } from 'react'
import { Info, ChevronDown, ChevronRight, Minus } from 'lucide-react'
import useAudienceTargetingStore from '@/store/audienceTargetingStore'
import { Checkbox } from '@/components/ui/checkbox'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip.jsx'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import LayersIcon from '@/components/icons/LayersIcon'
import DesktopIcon from '@/components/icons/DesktopIcon'
import MobileIcon from '@/components/icons/MobileIcon'
import AppleIcon from '@/components/icons/AppleIcon'
import AndroidIcon from '@/components/icons/AndroidIcon'
import TableRowSkeleton from '@/components/common/TableRowSkeleton'
import MetricsPicker from '../MetricsPicker'
import { useColumnPreferences } from '../hooks/useColumnPreferences'
import { cn } from '@/lib/utils'

const DeviceControls = ({
    isIndeterminate,
    isChecked,
    isExpanded,
    handleDeviceToggle,
    handleExpandToggle,
    deviceId,
}) => {
    return (
        <div className="tw:flex tw:items-center tw:gap-3">
            {isIndeterminate ? (
                <div
                    className="tw:w-4 tw:h-4 tw:bg-blue-300 tw:rounded-xs tw:flex tw:items-center tw:justify-center tw:cursor-pointer"
                    onClick={(e) => {
                        e.stopPropagation()
                        handleDeviceToggle(deviceId)
                    }}
                >
                    <Minus className="tw:w-3 tw:h-3 tw:text-white" />
                </div>
            ) : (
                <div className="tw:flex tw:items-center" onClick={(e) => e.stopPropagation()}>
                    <Checkbox checked={isChecked} onCheckedChange={() => handleDeviceToggle(deviceId)} />
                </div>
            )}
            <button
                onClick={(e) => {
                    e.stopPropagation()
                    handleExpandToggle(deviceId)
                }}
                className="tw:p-1 tw:rounded tw:text-[#899CBD] hover:tw:text-white tw:transition-all tw:duration-200 tw:ease-in-out hover:tw:bg-gray-700/10"
            >
                <ChevronRight
                    className={cn('tw:w-4 tw:h-4 tw:transition-transform tw:duration-200 tw:ease-in-out', {
                        'tw:rotate-90 tw:text-blue-300': isExpanded,
                        'tw:rotate-0': !isExpanded,
                    })}
                />
            </button>
        </div>
    )
}

const Row = ({
    device,
    isExpanded,
    isChecked,
    isIndeterminate,
    handleDeviceToggle,
    handleExpandToggle,
    selectedDevices,
    selectedColumns,
    deviceMetrics,
    formatMetricValue,
}) => {
    const Icon = device.icon

    // Helper function to get metric value from deviceMetrics
    const getMetricValue = (apiKey, deviceId) => {
        const deviceKey = deviceId === 'mobile_ios' ? 'ios' : deviceId === 'mobile_android' ? 'android' : deviceId
        const metrics = deviceMetrics[deviceKey] || {}
        return metrics[apiKey] || 0
    }

    return (
        <>
            {/* Parent device row */}
            <TableRow
                className="hover:tw:bg-gray-700/20 tw:cursor-pointer"
                onClick={() => handleDeviceToggle(device.id)}
            >
                <TableCell className="tw:pl-3">
                    <div className={cn('tw:flex tw:items-center tw:gap-3', { 'tw:gap-1': device.hasSubDevices })}>
                        {device.hasSubDevices ? (
                            <DeviceControls
                                isIndeterminate={isIndeterminate}
                                isChecked={isChecked}
                                isExpanded={isExpanded}
                                handleDeviceToggle={handleDeviceToggle}
                                handleExpandToggle={handleExpandToggle}
                                deviceId={device.id}
                            />
                        ) : (
                            <div className="tw:flex tw:items-center" onClick={(e) => e.stopPropagation()}>
                                <Checkbox checked={isChecked} onCheckedChange={() => handleDeviceToggle(device.id)} />
                            </div>
                        )}
                        <div className="tw:flex tw:items-center tw:gap-1">
                            <Icon className="tw:w-5 tw:h-5 tw:text-[#899CBD]" />
                            <span className="tw:text-sm tw:text-white">{device.label}</span>
                        </div>
                    </div>
                </TableCell>
                {/* Dynamic metric columns */}
                {selectedColumns.map((column) => {
                    const value = getMetricValue(column.apiKey, device.id)
                    const formattedValue = formatMetricValue(value, column)

                    return (
                        <TableCell key={column.id} className="tw:text-right tw:text-sm tw:text-white tw:pr-3">
                            {formattedValue}
                        </TableCell>
                    )
                })}
            </TableRow>

            {/* Sub-device rows */}
            {device.hasSubDevices &&
                isExpanded &&
                device.subDevices.map((subDevice) => {
                    const SubIcon = subDevice.icon

                    return (
                        <TableRow
                            key={subDevice.id}
                            className="hover:tw:bg-gray-700/20 tw:cursor-pointer tw:animate-in tw:slide-in-from-top-1 tw:duration-200"
                            onClick={() => handleDeviceToggle(subDevice.id)}
                        >
                            <TableCell className="tw:pl-3">
                                <div className="tw:flex tw:items-center tw:gap-3 tw:pl-8">
                                    <div className="tw:flex tw:flex-col" onClick={(e) => e.stopPropagation()}>
                                        <Checkbox
                                            checked={selectedDevices.includes(subDevice.id)}
                                            onCheckedChange={() => handleDeviceToggle(subDevice.id)}
                                        />
                                    </div>
                                    <div className="tw:flex tw:items-center tw:gap-1">
                                        <SubIcon className="tw:w-4 tw:h-4 tw:text-[#899CBD]" />
                                        <span className="tw:text-sm tw:text-white">{subDevice.label}</span>
                                    </div>
                                </div>
                            </TableCell>
                            {/* Dynamic metric columns for sub-devices */}
                            {selectedColumns.map((column) => {
                                const value = getMetricValue(column.apiKey, subDevice.id)
                                const formattedValue = formatMetricValue(value, column)

                                return (
                                    <TableCell
                                        key={column.id}
                                        className="tw:text-right tw:text-sm tw:text-white tw:pr-3"
                                    >
                                        {formattedValue}
                                    </TableCell>
                                )
                            })}
                        </TableRow>
                    )
                })}
        </>
    )
}

const DeviceSettings = ({
    stage,
    audience,
    metricsDataLoading,
    formatMetricValue,
    metricsData,
    setStepContentData,
    getStepContentData,
}) => {
    const { getAudienceSettings, updateAudienceSetting } = useAudienceTargetingStore()
    const [selectedDevices, setSelectedDevices] = useState([])
    const [expandedDevices, setExpandedDevices] = useState(['mobile'])

    // Column preferences hook
    const {
        selectedColumns,
        allMetrics,
        updateSelectedColumns,
        isLoading: allMetricsLoading,
    } = useColumnPreferences('Device', setStepContentData, getStepContentData)

    // Handle metric replacement for a specific column index
    const handleMetricReplace = (columnIndex, newMetric) => {
        const currentMetrics = [...selectedColumns]
        currentMetrics[columnIndex] = newMetric
        updateSelectedColumns(currentMetrics)
    }

    // Process metrics data to organize by device and metric
    const deviceMetrics = useMemo(() => {
        const metrics = {}
        if (metricsData) {
            metricsData.forEach((metric) => {
                const device = metric.breakdownValue.toLowerCase()
                if (!metrics[device]) metrics[device] = {}
                // Store all metric values by their API key
                metrics[device][metric.metricApiKey] = metric.value
            })
        }

        // Calculate mobile totals from iOS and Android for each metric
        const mobileMetrics = {}
        Object.keys(metrics.ios || {}).forEach((metricKey) => {
            if (metricKey === 'spend' || metrics.ios?.[metricKey]?.dataType === 'currency') {
                mobileMetrics[metricKey] = (metrics.ios?.[metricKey] || 0) + (metrics.android?.[metricKey] || 0)
            } else {
                // For rates/percentages, use iOS value or Android if iOS not available
                mobileMetrics[metricKey] = metrics.ios?.[metricKey] || metrics.android?.[metricKey] || 0
            }
        })
        Object.keys(metrics.android || {}).forEach((metricKey) => {
            if (!mobileMetrics[metricKey]) {
                mobileMetrics[metricKey] = metrics.android[metricKey]
            }
        })

        return {
            ...metrics,
            mobile: mobileMetrics,
        }
    }, [metricsData])

    // Device options with hierarchical structure
    const deviceOptions = [
        {
            id: 'desktop',
            label: 'Desktop',
            icon: DesktopIcon,
            hasSubDevices: false,
        },
        {
            id: 'mobile',
            label: 'Mobile',
            icon: MobileIcon,
            hasSubDevices: true,
            subDevices: [
                {
                    id: 'mobile_ios',
                    label: 'iOS',
                    icon: AppleIcon,
                },
                {
                    id: 'mobile_android',
                    label: 'Android',
                    icon: AndroidIcon,
                },
            ],
        },
    ]

    // Get current settings
    const audienceSettings = getAudienceSettings(stage, audience)
    const storedDevices = audienceSettings?.device_targets

    // Initialize from stored value
    useEffect(() => {
        if (storedDevices && Array.isArray(storedDevices)) {
            setSelectedDevices(storedDevices)
        } else {
            // Default to all devices and sub-devices selected
            const defaultDevices = ['desktop', 'mobile_ios', 'mobile_android']
            setSelectedDevices(defaultDevices)
            updateAudienceSetting(stage, audience, 'device_targets', defaultDevices)
        }
    }, [storedDevices, stage, audience, updateAudienceSetting])

    const handleDeviceToggle = (deviceId) => {
        let newSelection = [...selectedDevices]

        if (deviceId === 'mobile') {
            // Toggle all mobile sub-devices
            const mobileSubDevices = ['mobile_ios', 'mobile_android']
            const allMobileSelected = mobileSubDevices.every((sub) => selectedDevices.includes(sub))

            if (allMobileSelected) {
                newSelection = newSelection.filter((d) => !mobileSubDevices.includes(d))
            } else {
                mobileSubDevices.forEach((sub) => {
                    if (!newSelection.includes(sub)) {
                        newSelection.push(sub)
                    }
                })
            }
        } else {
            // Toggle individual device
            if (newSelection.includes(deviceId)) {
                newSelection = newSelection.filter((d) => d !== deviceId)
            } else {
                newSelection.push(deviceId)
            }
        }

        // Don't allow all devices to be deselected
        if (newSelection.length === 0) return

        setSelectedDevices(newSelection)
        updateAudienceSetting(stage, audience, 'device_targets', newSelection)
    }

    const handleExpandToggle = (deviceId) => {
        setExpandedDevices((prev) =>
            prev.includes(deviceId) ? prev.filter((d) => d !== deviceId) : [...prev, deviceId],
        )
    }

    const isDeviceChecked = (device) => {
        if (device.hasSubDevices) {
            const subDeviceIds = device.subDevices.map((sub) => sub.id)
            return subDeviceIds.every((subId) => selectedDevices.includes(subId))
        }
        return selectedDevices.includes(device.id)
    }

    const isDeviceIndeterminate = (device) => {
        if (device.hasSubDevices) {
            const subDeviceIds = device.subDevices.map((sub) => sub.id)
            const selectedSubDevices = subDeviceIds.filter((subId) => selectedDevices.includes(subId))
            return selectedSubDevices.length > 0 && selectedSubDevices.length < subDeviceIds.length
        }
        return false
    }

    return (
        <div className="tw:flex tw:gap-6 tw:flex-col tw:bg-secondary-background tw:rounded-xs">
            <div className="tw:flex tw:gap-1.5 tw:items-center tw:p-3 tw:pb-0">
                <LayersIcon className="tw:w-6 tw:h-6 tw:text-gray-200" />
                <span className="tw:font-semibold">Device, platform and placement</span>
                <Tooltip>
                    <TooltipTrigger>
                        <Info className="tw:w-4 tw:h-4 tw:text-gray-200" />
                    </TooltipTrigger>
                    <TooltipContent showArrow={false}>
                        <div className="tw:text-sm tw:text-gray-400">
                            Target your ads based on device types.
                            <br />
                            <span className="tw:text-white tw:font-medium tw:block tw:mt-2">
                                At least one device must be selected
                            </span>
                        </div>
                    </TooltipContent>
                </Tooltip>
            </div>

            <div className="">
                <Table className="tw:[&_tbody_tr:nth-child(even)]:bg-[#142234]">
                    <TableHeader>
                        <TableRow>
                            <TableHead className="tw:text-grap-200 tw:pl-3">Device</TableHead>
                            {selectedColumns.map((column, index) => (
                                <TableHead key={column.id} className="tw:text-[#899CBD] tw:!text-right tw:pr-3">
                                    <MetricsPicker
                                        allMetrics={allMetrics}
                                        currentMetric={column}
                                        onMetricSelect={(metric) => handleMetricReplace(index, metric)}
                                    >
                                        <button
                                            className="tw:flex tw:items-center tw:justify-end tw:gap-1 hover:tw:text-white tw:transition-colors tw:duration-200 tw:ease-in-out tw:cursor-pointer tw:w-full"
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            <span>{column.name}</span>
                                            <div className="tw:flex tw:items-center tw:gap-1">
                                                <ChevronDown className="tw:w-4 tw:h-4" />
                                            </div>
                                        </button>
                                    </MetricsPicker>
                                </TableHead>
                            ))}
                        </TableRow>
                    </TableHeader>
                    {metricsDataLoading || allMetricsLoading ? (
                        <TableRowSkeleton
                            rows={4}
                            showIcon={true}
                            showCheckbox={true}
                            columns={selectedColumns.length + 1}
                        />
                    ) : (
                        <TableBody>
                            {deviceOptions.map((device) => (
                                <Row
                                    key={device.id}
                                    device={device}
                                    deviceMetrics={deviceMetrics}
                                    selectedColumns={selectedColumns}
                                    selectedDevices={selectedDevices}
                                    isChecked={isDeviceChecked(device)}
                                    formatMetricValue={formatMetricValue}
                                    handleDeviceToggle={handleDeviceToggle}
                                    handleExpandToggle={handleExpandToggle}
                                    isIndeterminate={isDeviceIndeterminate(device)}
                                    isExpanded={expandedDevices.includes(device.id)}
                                />
                            ))}
                        </TableBody>
                    )}
                </Table>
            </div>
        </div>
    )
}

export default DeviceSettings
